import React, { useState } from 'react';
import { Category, Characteristic } from '../../types/categories';

interface MultiSelectChipsProps {
  items: (Category | Characteristic)[];
  selectedIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const MultiSelectChips: React.FC<MultiSelectChipsProps> = ({
  items,
  selectedIds,
  onSelectionChange,
  placeholder = "Selecione as opções...",
  disabled = false,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Filtrar itens baseado no termo de busca
  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Verificar se um item está selecionado
  const isSelected = (itemId: string) => selectedIds.includes(itemId);

  // Toggle seleção de um item
  const toggleSelection = (itemId: string) => {
    if (disabled) return;
    
    const newSelection = isSelected(itemId)
      ? selectedIds.filter(id => id !== itemId)
      : [...selectedIds, itemId];
    
    onSelectionChange(newSelection);
  };

  // Obter itens selecionados para exibição
  const selectedItems = items.filter(item => selectedIds.includes(item.id));


  return (
    <div className={`relative ${className}`}>
      {/* Campo de entrada */}
      <div
        className={`
          min-h-[42px] px-3 py-2 border-2 border-gray-200 rounded-xl cursor-pointer
          flex flex-wrap gap-2 items-center
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-gray-300'}
          ${isOpen ? 'border-blue-500 ring-2 ring-blue-200' : ''}
          transition-all duration-200
        `}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        {selectedItems.length === 0 ? (
          <span className="text-gray-500 text-sm">{placeholder}</span>
        ) : (
          <div className="flex flex-wrap gap-2">
            {selectedItems.map(item => (
              <div
                key={item.id}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium"
                style={{ 
                  border: `1px solid ${item.color}40`
                }}
              >
                <span>{item.name}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSelection(item.id);
                    }}
                    className="ml-1 hover:bg-black hover:bg-opacity-10 rounded-full p-0.5 transition-colors"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        
        {/* Ícone de dropdown */}
        <div className="ml-auto">
          <svg 
            className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg max-h-60 overflow-hidden">
          {/* Campo de busca */}
          <div className="p-3 border-b border-gray-100">
            <input
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Lista de itens */}
          <div className="max-h-48 overflow-y-auto">
            {filteredItems.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                Nenhum item encontrado
              </div>
            ) : (
              filteredItems.map(item => (
                <div
                  key={item.id}
                  className={`
                    px-4 py-3 cursor-pointer flex items-center gap-3 hover:bg-gray-50
                    transition-colors duration-150
                    ${isSelected(item.id) ? 'bg-blue-50' : ''}
                  `}
                  onClick={() => toggleSelection(item.id)}
                >
                  {/* Checkbox */}
                  <div className="flex-shrink-0">
                    <div className={`
                      w-4 h-4 border-2 rounded flex items-center justify-center
                      ${isSelected(item.id) 
                        ? 'bg-blue-500 border-blue-500' 
                        : 'border-gray-300'
                      }
                    `}>
                      {isSelected(item.id) && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>

                  {/* Conteúdo */}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-gray-900 truncate">
                      {item.name}
                    </div>
                    {item.description && (
                      <div className="text-xs text-gray-500 truncate">
                        {item.description}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Overlay para fechar dropdown */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default MultiSelectChips;
